/**
 * The art index, kept on the device.
 *
 * ADR 0004 decided the search structure is a generated artifact rather than a SQL
 * index, and that the client downloads it so a scan does not depend on
 * connectivity. This module is the download: the second kind of artifact the same
 * update path installs, and the one a scan reads instead of 1.6 MB of fingerprints
 * crossing the wire on every capture.
 *
 * Three things are deliberately different from the web bundle next door:
 *
 *  - **It is one file.** The app is a directory the shell serves, so its install is a
 *    directory swap; the index is a single blob, so its install is one rename - and a
 *    rename within a directory is atomic, so a device that loses power either has the
 *    old index or the new one, never half of either.
 *  - **Nothing counts boots.** The shell counts launches because a bundle that fails
 *    to come up cannot report the failure. A bad index is not executable: it is
 *    noticed by the code that reads it, which falls back to the server and says so.
 *    There is nothing to roll back *to*, and the next launch re-downloads.
 *  - **It lives in its own directory.** `art-index/` under the app's private data, so
 *    the shell's bundle staging and its `state.json` cannot be confused with the
 *    index's - a mistake that would have the shell trying to boot a fingerprint file.
 *
 * The version in `state.json` is the artifact's content address, so "is there a newer
 * index?" is the same string comparison the app bundle's update does, and a rebuild
 * that changed nothing downloads nothing.
 */

import { decodeArtIndex, type ArtIndex } from '@unsorted/shared';
import {
  createUpdater,
  type UpdateStateListener,
  type UpdateStore,
  type Updater,
} from '@unsorted/web-update';
import { fromBase64, toBase64 } from '../base64.js';
import { DATA_DIRECTORY, type CapacitorBridge, type FilesystemPlugin } from './host.js';

/**
 * The file the artifact holds, and the name the server publishes it under.
 *
 * One file, so a manifest naming another is refused rather than staged: the reader
 * looks at exactly this path, and a store that wrote whatever it was handed would
 * install a file nothing ever reads.
 */
export const ART_INDEX_FILE = 'art-index.bin';

/** Where the index and its pointer live, inside the app's private data directory. */
export const ART_INDEX_DIRECTORY = 'art-index';

/** The published artifact's manifest, on the same origin as the API. */
export const ART_INDEX_MANIFEST_URL = '/api/updates/art/manifest.json';

/** The downloaded file, before the rename that makes it the one the app reads. */
const STAGED_FILE = `${ART_INDEX_FILE}.staging`;

/** The pointer: which version the installed file is. */
const STATE_FILE = 'state.json';

/** What `running()` says on a device that has never installed an index. */
export const NO_ART_INDEX = 'none';

const under = (name: string): string => `${ART_INDEX_DIRECTORY}/${name}`;

/** What `state.json` holds. Read as untrusted input: a half-write is a parse failure. */
interface IndexState {
  version: string;
}

async function readState(files: FilesystemPlugin): Promise<IndexState | null> {
  try {
    const file = await files.readFile({ path: under(STATE_FILE), directory: DATA_DIRECTORY });
    const parsed: unknown = JSON.parse(file.data);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { version } = parsed as { version?: unknown };
    return typeof version === 'string' && version !== '' ? { version } : null;
  } catch {
    // No pointer is the ordinary state of a device that has never updated: the app
    // scans against the server, exactly as it did before this artifact existed.
    return null;
  }
}

/**
 * The install, through the filesystem plugin.
 *
 * `stage` writes the verified bytes under a name nothing reads, `activate` renames
 * that file over the installed one, and the pointer is written last - so the window
 * in which the device has no index is a window in which it still has the previous
 * one, and the only way to lose the index entirely is to have never had it.
 */
export function createArtifactStore(files: FilesystemPlugin): UpdateStore {
  return {
    stage: async (_version, path, bytes) => {
      if (path !== ART_INDEX_FILE) {
        throw new Error(`the art index artifact is ${ART_INDEX_FILE}, and the manifest names ${path}`);
      }
      await files.writeFile({
        path: under(STAGED_FILE),
        data: toBase64(bytes),
        encoding: 'base64',
        directory: DATA_DIRECTORY,
        recursive: true,
      });
    },

    discardStaged: async () => {
      try {
        await files.rmdir({ path: under(STAGED_FILE), directory: DATA_DIRECTORY });
      } catch {
        // A staged file that is not there is the normal case after a successful
        // install, and a failed update that cannot tidy up is not worth reporting.
      }
    },

    activate: async (version) => {
      await files.rename({
        from: under(STAGED_FILE),
        to: under(ART_INDEX_FILE),
        directory: DATA_DIRECTORY,
        toDirectory: DATA_DIRECTORY,
      });
      await files.writeFile({
        path: under(STATE_FILE),
        data: `${JSON.stringify({ version })}\n`,
        encoding: 'utf8',
        directory: DATA_DIRECTORY,
      });
    },

    running: async () => (await readState(files))?.version ?? NO_ART_INDEX,
  };
}

export interface ArtIndexUpdateOptions {
  bridge: CapacitorBridge | null;
  /** The filesystem plugin, or undefined to look it up on the bridge. */
  files?: FilesystemPlugin;
  /** The published manifest. Defaults to this origin's API. */
  manifestUrl?: string;
  fetchImpl?: typeof fetch;
  onState?: UpdateStateListener;
}

/**
 * The index updater, or null when this is not somewhere an index can be installed.
 *
 * Null for a browser tab for the same reason the bundle updater is: what it installs
 * is a file in the app's private storage, and a page has none. A browser tab still
 * matches against the server, which is what it did before.
 */
export async function artIndexUpdater(options: ArtIndexUpdateOptions): Promise<Updater | null> {
  const { bridge } = options;
  if (bridge === null || !bridge.isNative()) return null;
  const files = options.files ?? bridge.plugin<FilesystemPlugin>('Filesystem');
  if (files === undefined) return null;

  return createUpdater({
    kind: 'art',
    manifestUrl: options.manifestUrl ?? ART_INDEX_MANIFEST_URL,
    store: createArtifactStore(files),
    ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
    ...(options.onState === undefined ? {} : { onState: options.onState }),
  });
}

export interface InstalledArtIndex {
  /** The artifact's version, which is what `state.json` records. */
  version: string;
  index: ArtIndex;
}

/**
 * The index this app can reach, through whichever filesystem the app has.
 *
 * The one call a screen makes: in a browser tab there is no bridge and no artifact,
 * so this is nothing, and the scan matches against the server exactly as it did
 * before the artifact existed. A loader rather than a value because it reads a file,
 * and a screen should ask for it when it needs it rather than at import time.
 */
export async function installedArtIndexFromBridge(
  bridge: CapacitorBridge | null,
): Promise<InstalledArtIndex | null> {
  const files = bridge?.plugin<FilesystemPlugin>('Filesystem');
  if (files === undefined) return null;
  return loadInstalledArtIndex(files);
}

/**
 * The index this device has, or null when it has none it can use.
 *
 * Null rather than an error, because every caller's response is the same: match
 * against the server instead. A file that does not decode - an interrupted install on
 * a filesystem that reordered a rename, a version of the format this build does not
 * read - is reported on the console for whoever is holding the phone, and treated by
 * the app as "no local index yet".
 */
export async function loadInstalledArtIndex(
  files: FilesystemPlugin,
): Promise<InstalledArtIndex | null> {
  const state = await readState(files);
  if (state === null) return null;
  try {
    const file = await files.readFile({
      path: under(ART_INDEX_FILE),
      directory: DATA_DIRECTORY,
      encoding: 'base64',
    });
    return { version: state.version, index: decodeArtIndex(fromBase64(file.data)) };
  } catch (error) {
    console.warn('the art index on this device could not be read', error);
    return null;
  }
}
